import dayjs from 'dayjs';

import { TiendaPerfectaVisitHistoryStop } from 'models/TiendaPerfecta';

import {
  MUTED_TEAL,
  SLATE_BLUE,
  SLATE_DARK,
  SOFT_PURPLE,
  TEAL,
} from './palette';
import { VisitSelection } from './selection';

/** Route executions above this many start with only the last day visible. */
export const INITIAL_VISIBLE_LIMIT = 20;

/**
 * The approved base hues of the vans: Teal, Slate Blue, Soft Purple, Muted
 * Teal and Slate Dark. None is Navy, so a van never reads as "executed".
 */
export const ROUTE_BASE_HUES = [
  TEAL,
  SLATE_BLUE,
  SOFT_PURPLE,
  MUTED_TEAL,
  SLATE_DARK,
];

/**
 * Van palette: the 5 base hues, then a lighter and a darker step of each (15
 * distinct colors) so a fleet of 14 vans never repeats a base color. Slate
 * Dark has no visible darker step, so it takes a much lighter one instead.
 */
export const ROUTE_PALETTE = [
  ...ROUTE_BASE_HUES,
  ...ROUTE_BASE_HUES.map((hue) => mixWith(hue, 0.4)),
  ...ROUTE_BASE_HUES.map((hue) =>
    hue === SLATE_DARK ? mixWith(hue, 0.7) : mixWith(hue, -0.3)
  ),
];

/**
 * The base color of each van (palette by van id order). The map tones it per
 * day; the consolidated table shows it as is, so a van reads the same in both.
 */
export const vanColorMap = (
  stops: Pick<TiendaPerfectaVisitHistoryStop, 'van'>[]
): Map<string, string> =>
  new Map(
    [...new Set(stops.map((s) => s.van.id))]
      .sort()
      .map((id, index) => [id, ROUTE_PALETTE[index % ROUTE_PALETTE.length]])
  );

/** Mix with white (positive) or black (negative), per day of the range. */
const TONE_STEPS = [0, 0.22, -0.2, 0.38, -0.32, 0.5, -0.42];

/**
 * `hex` mixed with white (positive `step`) or black (negative), as a
 * lowercase hex. Deterministic; `0` returns the color unchanged.
 */
function mixWith(hex: string, step: number): string {
  const channel = (offset: number) =>
    parseInt(hex.slice(offset, offset + 2), 16);
  const target = step >= 0 ? 255 : 0;
  const amount = Math.abs(step);

  return (
    '#' +
    [1, 3, 5]
      .map((offset) => {
        const value = channel(offset);

        return Math.round(value + (target - value) * amount)
          .toString(16)
          .padStart(2, '0');
      })
      .join('')
  );
}

/**
 * The tone of `hex` for the n-th day of the range (0 = the base color), so a
 * van stays recognizable all week while each day looks different.
 */
export const toneOf = (hex: string, dayIndex: number): string =>
  mixWith(hex, TONE_STEPS[dayIndex % TONE_STEPS.length]);

export type PinState = 'executed' | 'incident' | 'pending';

export interface MapPoint {
  stop: TiendaPerfectaVisitHistoryStop;
  lat: number;
  lng: number;
  /** The PLANNED sequence, whatever order the map draws. */
  number: number;
  state: PinState;
}

export interface RouteExecution {
  /** `routeId|plannedDate`. */
  key: string;
  routeId: string;
  routeCode: string;
  date: string;
  vanId: string;
  vanName: string;
  zoneId: string;
  zoneName: string;
  color: string;
  stops: TiendaPerfectaVisitHistoryStop[];
  /** Stops with a location, by planned sequence. */
  plannedOrder: MapPoint[];
  /**
   * Stops with a location that finished, by finish time and then start time.
   * A stop that has not finished is a pin but not part of the path.
   */
  executedOrder: MapPoint[];
  executed: number;
  planned: number;
  withoutLocation: number;
}

const instant = (iso: string | null): number =>
  iso === null ? Number.POSITIVE_INFINITY : Date.parse(iso);

const toPoint = (stop: TiendaPerfectaVisitHistoryStop): MapPoint | null =>
  stop.location
    ? {
        stop,
        lat: stop.location.lat,
        lng: stop.location.lng,
        number: stop.sequence,
        state: stop.outcome,
      }
    : null;

/**
 * One entry per route execution (`route.id + plannedDate`) found in the flat
 * stops, ordered by date and route code. Colors: one palette entry per van (in
 * van id order) and one tone per day of the range.
 */
export const buildRouteExecutions = (
  stops: TiendaPerfectaVisitHistoryStop[]
): RouteExecution[] => {
  const groups = new Map<string, TiendaPerfectaVisitHistoryStop[]>();
  stops.forEach((stop) => {
    const key = `${stop.route.id}|${stop.plannedDate}`;
    const group = groups.get(key);
    if (group) group.push(stop);
    else groups.set(key, [stop]);
  });

  const vanColors = vanColorMap(stops);
  const dates = [...new Set(stops.map((s) => s.plannedDate))].sort();

  return [...groups.entries()]
    .map(([key, group]): RouteExecution => {
      const first = group[0];
      const base = vanColors.get(first.van.id) as string;
      const ordered = [...group].sort((a, b) => a.sequence - b.sequence);
      const points = ordered
        .map(toPoint)
        .filter((p): p is MapPoint => p !== null);

      return {
        key,
        routeId: first.route.id,
        routeCode: first.route.code,
        date: first.plannedDate,
        vanId: first.van.id,
        vanName: first.van.name,
        zoneId: first.zone.id,
        zoneName: first.zone.name,
        color: toneOf(base, dates.indexOf(first.plannedDate)),
        stops: ordered,
        plannedOrder: points,
        executedOrder: points
          .filter((p) => p.stop.finishedAt !== null)
          .sort(
            (a, b) =>
              instant(a.stop.finishedAt) - instant(b.stop.finishedAt) ||
              instant(a.stop.startedAt) - instant(b.stop.startedAt) ||
              a.number - b.number
          ),
        executed: group.filter((s) => s.outcome === 'executed').length,
        planned: group.length,
        withoutLocation: group.length - points.length,
      };
    })
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) || a.routeCode.localeCompare(b.routeCode)
    );
};

/**
 * Which route executions start visible: all of them, unless there are more
 * than `limit`; then only those of the last day of the range.
 */
export const initialVisibleKeys = (
  executions: RouteExecution[],
  limit = INITIAL_VISIBLE_LIMIT
): Set<string> => {
  if (executions.length <= limit) return new Set(executions.map((e) => e.key));

  const lastDay = executions.reduce(
    (max, e) => (e.date > max ? e.date : max),
    ''
  );

  return new Set(
    executions.filter((e) => e.date === lastDay).map((e) => e.key)
  );
};

export const executionMatchesSelection = (
  execution: RouteExecution,
  selection: VisitSelection
): boolean => {
  if (!selection) return true;

  switch (selection.kind) {
    case 'day':
      return execution.date === selection.date;
    case 'route':
      return (
        execution.routeId === selection.routeId &&
        execution.date === selection.date
      );
    case 'van':
      return execution.vanId === selection.vanId;
    case 'zone':
      return execution.zoneId === selection.zoneId;
  }
};

export const visiblePoints = (
  executions: RouteExecution[],
  visible: Set<string>
): MapPoint[] =>
  executions.filter((e) => visible.has(e.key)).flatMap((e) => e.plannedOrder);

export const withoutLocationCount = (
  executions: RouteExecution[],
  visible: Set<string>
): number =>
  executions
    .filter((e) => visible.has(e.key))
    .reduce((sum, e) => sum + e.withoutLocation, 0);

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export const computeBounds = (
  points: Pick<MapPoint, 'lat' | 'lng'>[]
): MapBounds | null => {
  if (points.length === 0) return null;

  return points.reduce<MapBounds>(
    (b, p) => ({
      north: Math.max(b.north, p.lat),
      south: Math.min(b.south, p.lat),
      east: Math.max(b.east, p.lng),
      west: Math.min(b.west, p.lng),
    }),
    {
      north: points[0].lat,
      south: points[0].lat,
      east: points[0].lng,
      west: points[0].lng,
    }
  );
};

/** `Lun 28 sep`: short weekday, day and month, capitalized. */
export const dayHeaderLabel = (date: string, language: string): string => {
  const label = dayjs(date)
    .locale(language.startsWith('es') ? 'es' : 'en')
    .format('ddd D MMM')
    .replace(/\./g, '');

  return label.charAt(0).toUpperCase() + label.slice(1);
};
